import { useEffect, useState } from "react";
import type { VisitReport } from "../../api/routing";
import { Segmented } from "../../components/ui";
import { useDebounced } from "../../hooks/useDebounced";
import { PERIODS, type ReportPeriod, type ReportQuery } from "./reportQuery";

/** The period and the filters the visits report and the visits dashboard share,
 * so the two screens always describe the same visits. */
export function ReportControls({
  query,
  report,
  onChange,
}: {
  query: ReportQuery;
  report: VisitReport | undefined;
  onChange: (change: Partial<ReportQuery>) => void;
}) {
  const [typed, setTyped] = useState(query.q);
  const settled = useDebounced(typed, 300);
  useEffect(() => {
    if (settled !== query.q) {
      onChange({ q: settled });
    }
    // Only the settled text starts a search; the query it lands in is current.
  }, [settled]);
  const period = report?.period;
  const options = report?.options;
  const filtered = [query.provider_id, query.outcome, query.plan_id, query.region_id, query.q].some((v) => v !== "");

  function choosePeriod(next: ReportPeriod) {
    // A range starts as the period on screen, so switching loses nothing.
    onChange(
      next === "range" && period
        ? { period: next, start: period.start, end: period.end }
        : { period: next, start: "", end: "" },
    );
  }

  return (
    <section className="card report-controls" aria-label="Report period and filters">
      <div className="report-row">
        <Segmented label="Period" options={PERIODS} value={query.period} onChange={choosePeriod} />
        {query.period === "range" ? (
          <>
            <label className="field">
              From
              <input type="date" value={query.start || period?.start || ""} onChange={(e) => onChange({ start: e.target.value })} />
            </label>
            <label className="field">
              To
              <input type="date" value={query.end || period?.end || ""} onChange={(e) => onChange({ end: e.target.value })} />
            </label>
          </>
        ) : (
          <>
            <label className="field">
              {query.period === "day" ? "Day" : query.period === "week" ? "Week of" : "Month of"}
              <input type="date" value={query.on || report?.today || ""} onChange={(e) => onChange({ on: e.target.value })} />
            </label>
            <div className="report-steps">
              <button type="button" className="btn" disabled={!period?.prev_on} onClick={() => onChange({ on: period?.prev_on ?? "" })}>
                ‹ Previous
              </button>
              <button type="button" className="btn" onClick={() => onChange({ on: "" })}>
                {query.period === "day" ? "Today" : query.period === "week" ? "This week" : "This month"}
              </button>
              <button type="button" className="btn" disabled={!period?.next_on} onClick={() => onChange({ on: period?.next_on ?? "" })}>
                Next ›
              </button>
            </div>
          </>
        )}
      </div>
      <div className="report-row">
        <label className="field">
          Field operators
          <select value={query.provider_id} onChange={(e) => onChange({ provider_id: e.target.value })}>
            <option value="">Every field operator</option>
            {options?.crews.map((crew) => (
              <option key={crew.id} value={crew.id}>
                {crew.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          Outcome
          <select value={query.outcome} onChange={(e) => onChange({ outcome: e.target.value })}>
            <option value="">Every outcome</option>
            {options?.outcomes.map((outcome) => (
              <option key={outcome.value} value={outcome.value}>
                {outcome.label}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          Plan
          <select value={query.plan_id} onChange={(e) => onChange({ plan_id: e.target.value })}>
            <option value="">Every plan</option>
            {options?.plans.map((plan) => (
              <option key={plan.id} value={plan.id}>
                {plan.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          Region
          <select value={query.region_id} onChange={(e) => onChange({ region_id: e.target.value })}>
            <option value="">Every region</option>
            {options?.regions.map((region) => (
              <option key={region.id} value={region.id}>
                {region.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field report-search">
          Site or work order
          <input type="search" value={typed} placeholder="Camps Bay, 511029…" onChange={(e) => setTyped(e.target.value)} />
        </label>
        {filtered ? (
          <button
            type="button"
            className="btn"
            onClick={() => {
              setTyped("");
              onChange({ provider_id: "", outcome: "", plan_id: "", region_id: "", q: "" });
            }}
          >
            Clear filters
          </button>
        ) : null}
      </div>
    </section>
  );
}
